<template>
  <div style="overflow: scroll; height: calc(100vh - 75px);">
    <Container>
      <div v-if="loading" style="margin-top: 4rem; text-align: center;">
        Loading...
      </div>
      <template v-else-if="html">
        <!-- Edit mode for owners on draft versions -->
        <div v-if="canEdit" style="margin-bottom: 2rem; margin-top: 2rem;">
          <div class="tw-flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold">{{ policyLabel }} — Draft preview</h2>
            <div class="tw-flex gap-2">
              <ds-button
                v-if="!editing"
                label="Edit"
                variant="rounded"
                size="extra-small"
                icon="edit"
                @click="editing = true"
              />
              <template v-else>
                <ds-button
                  label="Save"
                  variant="rounded"
                  size="extra-small"
                  :disabled="saving"
                  @click="saveMarkdown"
                />
                <ds-button
                  label="Cancel"
                  variant="rounded-minimal"
                  size="extra-small"
                  @click="cancelEdit"
                />
              </template>
            </div>
          </div>
          <div v-if="editing">
            <rich-text-area
              v-model="editableHtml"
              :full-height="true"
              :no-height-limit="true"
            />
          </div>
          <div v-else v-html="html"></div>
        </div>
        <!-- Read-only view -->
        <div v-else style="margin-bottom: 2rem; margin-top: 4rem;">
          <div v-if="versionName" class="policy-meta">
            <span class="policy-meta__version">{{ policyLabel }} — {{ versionName }}</span>
            <span v-if="activeFrom" class="policy-meta__date">Effective {{ formatDate(activeFrom) }}</span>
          </div>
          <div v-html="html"></div>
        </div>
      </template>
      <div v-else style="margin-top: 4rem; text-align: center;">
        <p>No {{ policyLabel }} has been configured yet.</p>
      </div>
    </Container>
  </div>
</template>
<script lang="ts">
  import { defineComponent } from 'vue'
  import { Policy } from '@/Domain/Policy/Enums/enums'
  import { fetchActiveByType, updateContentByType } from '@/Domain/Policy/Api/legal-policies'
  import Container from '@/components/Container/Container.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'

  export default defineComponent({
    name: "Policy",
    components: { Container, DsButton, RichTextArea },
    data() {
      return {
        html: '',
        editableHtml: '',
        loading: true,
        editing: false,
        saving: false,
        isDraft: false,
        versionName: '',
        activeFrom: '',
      }
    },
    computed: {
      currentPolicy(): Policy | undefined {
        return (Object.values(Policy) as string[]).includes(this.$route.name)
          ? (this.$route.name as Policy)
          : undefined;
      },
      policyLabel() {
        switch (this.$route.name) {
          case Policy.termsOfUse:
            return 'Terms Of Use'
          case Policy.privacyPolicy:
            return 'Privacy Policy'
          case Policy.cookiePolicy:
            return 'Cookie Policy'
          default:
            return 'policy'
        }
      },
      versionId(): string | undefined {
        return this.$route.params.id as string | undefined
      },
      isOwner(): boolean {
        return this.$store.getters.isOwner
      },
      canEdit(): boolean {
        return this.isOwner && this.isDraft && !!this.versionId
      },
    },
    methods: {
      fetchPolicyPage() {
        if (!this.currentPolicy) {
          this.loading = false
          return
        }

        this.loading = true
        this.editing = false

        fetchActiveByType(this.currentPolicy, this.versionId)
          .then((response) => {
            const data = response.data

            if (data && data.link) {
              window.open(data.link, '_blank')
              this.$router.back()
              return
            }

            this.html = data && data.markdown ? data.markdown : ''
            this.isDraft = data && data.status === 'draft'
            this.versionName = data?.version_name || ''
            this.activeFrom = data?.active_from || ''
          })
          .catch(() => {
            this.html = ''
          })
          .finally(() => {
            this.loading = false
          })
      },
      formatDate(dateStr: string): string {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      },
      cancelEdit() {
        this.editing = false
        this.editableHtml = this.html
      },
      async saveMarkdown() {
        if (!this.currentPolicy || !this.versionId) return

        this.saving = true

        try {
          await updateContentByType(this.versionId, this.currentPolicy, this.editableHtml)
          this.html = this.editableHtml
          this.editing = false
        } catch (e) {
          console.error('Failed to save markdown', e)
          alert('Failed to save changes.')
        }

        this.saving = false
      },
    },
    mounted() {
      this.fetchPolicyPage()
    },
    watch: {
      currentPolicy() {
        this.fetchPolicyPage()
      },
      editing(val) {
        if (val) {
          this.editableHtml = this.html
        }
      },
    }
  })
</script>

<style scoped>
  .policy-meta {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .policy-meta__version {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .policy-meta__date {
    font-size: 13px;
    color: #666;
    margin-left: 12px;
  }
</style>
